/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { useMutation } from '@apollo/client'
import {
  Alert,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'

import {
  DELETE_DISPLAY,
  GET_TENANT_DISPLAYS,
  GetTenantDisplaysQueryVariables,
} from '@/graphql'
import useT from '@/i18n/useT'

interface Props {
  open: boolean
  onClose: () => void
  storeId: string
  displayId: string
  displayName: string
  refetchQueryVariables?: GetTenantDisplaysQueryVariables
  onSuccess?: () => void
}

export const DeleteDisplayDialog: React.FC<Props> = ({
  open,
  onClose,
  storeId,
  displayId,
  displayName,
  refetchQueryVariables,
  onSuccess,
}) => {
  const { t } = useT()

  const [deleteDisplay, { loading, error }] = useMutation(DELETE_DISPLAY, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_TENANT_DISPLAYS,
        variables: refetchQueryVariables ?? {
          filters: { store: { id: storeId } },
          pagination: { limit: 100, offset: 0 },
        },
      },
    ],
  })

  const handleDelete = async () => {
    try {
      await deleteDisplay({
        variables: {
          data: { id: displayId },
        },
      })
      onSuccess?.()
      onClose()
    } catch {
      // Apollo sets `error`
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('display.deleteTitle', 'Delete Display')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message}
          </Alert>
        )}
        <Typography>
          {t(
            'display.deleteConfirmMessage',
            'Are you sure you want to delete this display? This action cannot be undone.',
          )}
        </Typography>
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="subtitle1">{displayName}</Typography>
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={loading}
          loading={loading}
        >
          {t('common.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
